import type { LocalVideoTrack, Room } from 'livekit-client';
import { Track } from 'livekit-client';
import { readEconomyModeEnabled } from '@/lib/economy-mode-storage';
import {
  getEffectiveVideoQualityProfile,
  getVideoQualityRoomCapture,
  readVideoQualityProfile,
} from '@/lib/video-quality-profile';

/**
 * Em escritório com muitas calls/participantes na mesma rede, o uplink agregado
 * (N × maxBitrate da câmera) satura o AP antes do score "Poor" aparecer.
 * Capar o encode publicado reduz a carga compartilhada sem esperar congestão.
 *
 * Importante: todo cap precisa ter restore — senão o encoder fica preso baixo
 * e o score LiveKit continua "Poor" para sempre (espiral).
 *
 * REGRA CRÍTICA — nunca desativar camadas de simulcast (`active: false`) no cliente.
 * O SFU calcula o score do publisher com `DistanceToDesired()` = quantas camadas
 * espaciais faltam em relação ao que foi declarado no publish. Cada camada ausente
 * derruba um nível (`cDistanceWeight = 35`): 1 camada a menos → Good (amarelo),
 * 2 camadas a menos → **Poor (vermelho)** — mesmo com 0 % de perda de pacotes.
 * `LocalVideoTrack.prioritizePerformance()` do livekit-client deixa só a camada 0
 * ativa e por isso pinta o indicador de vermelho na hora em que a câmera liga.
 * Aqui só mexemos em `maxBitrate`/`maxFramerate`; as camadas continuam todas ativas
 * (o dynacast do SFU é quem pausa camadas sem assinante).
 */

/** Teto de publish em sala grande / rede compartilhada (~360p efetiva). */
export const LARGE_ROOM_CAMERA_MAX_BITRATE = 450_000;

/** Teto só quando ConnectionQuality é Lost (não Poor). */
export const POOR_NETWORK_CAMERA_MAX_BITRATE = 350_000;

/**
 * Teto "performance" — fundo virtual, CPU constrained, economia.
 * Substitui `prioritizePerformance()` (que desligava 2 das 3 camadas → Poor).
 */
export const PERFORMANCE_CAMERA_MAX_BITRATE = 400_000;
export const PERFORMANCE_CAMERA_MAX_FRAMERATE = 15;

/**
 * Teto da câmera enquanto ESTE cliente compartilha tela. O uplink passa a ser
 * dividido entre dois encoders de vídeo; a tela tem prioridade (é o conteúdo
 * que os outros estão olhando). Meet/Zoom fazem o mesmo: câmera cai para
 * thumbnail (~180–270p) durante a apresentação.
 */
export const SCREEN_SHARE_COEXIST_CAMERA_MAX_BITRATE = 300_000;
export const SCREEN_SHARE_COEXIST_CAMERA_MAX_FRAMERATE = 15;

export type CameraPublishBitrateCap = {
  maxBitrate: number;
  maxFramerate?: number;
  /**
   * Quando true, as camadas inferiores são reduzidas na mesma proporção da camada
   * alta (cap / maxBitrate original), em vez de só `Math.min`. Evita duas camadas
   * com o mesmo bitrate (desperdício de uplink) quando o cap fica abaixo da camada
   * intermediária.
   */
  proportional?: boolean;
};

type EncodingSnapshot = { maxBitrate?: number; maxFramerate?: number };

/** Valores originais do publish (antes do primeiro cap) — usados no restore. */
const originalEncodingsBySender = new WeakMap<RTCRtpSender, EncodingSnapshot[]>();

function getCameraSender(room: Room): RTCRtpSender | undefined {
  const pub = room.localParticipant.getTrackPublication(Track.Source.Camera);
  const sender = pub?.track?.sender;
  return sender && typeof sender.getParameters === 'function' ? sender : undefined;
}

function getTrackSender(track: LocalVideoTrack): RTCRtpSender | undefined {
  const sender = track.sender;
  return sender && typeof sender.getParameters === 'function' ? sender : undefined;
}

function rememberOriginalEncodings(sender: RTCRtpSender, encodings: RTCRtpEncodingParameters[]) {
  if (originalEncodingsBySender.has(sender)) return;
  originalEncodingsBySender.set(
    sender,
    encodings.map((enc) => ({ maxBitrate: enc.maxBitrate, maxFramerate: enc.maxFramerate })),
  );
}

/**
 * Ordena índices de encodings da camada mais alta para a mais baixa.
 * Usa `scaleResolutionDownBy` (1 = camada cheia) quando disponível; senão maxBitrate.
 */
function rankEncodingsHighToLow(encodings: RTCRtpEncodingParameters[]): number[] {
  const hasScale = encodings.some((enc) => typeof enc.scaleResolutionDownBy === 'number');
  return encodings
    .map((enc, index) => ({ enc, index }))
    .sort((a, b) => {
      if (hasScale) {
        return (a.enc.scaleResolutionDownBy ?? 1) - (b.enc.scaleResolutionDownBy ?? 1);
      }
      return (b.enc.maxBitrate ?? 0) - (a.enc.maxBitrate ?? 0);
    })
    .map(({ index }) => index);
}

async function capSenderEncodings(
  sender: RTCRtpSender,
  cap: CameraPublishBitrateCap,
): Promise<boolean> {
  try {
    const params = sender.getParameters();
    const encodings = params.encodings || [];
    if (encodings.length === 0) return false;

    rememberOriginalEncodings(sender, encodings);

    const activeBitrates = encodings
      .filter((enc) => enc.active !== false)
      .map((enc) => enc.maxBitrate ?? 0);
    const topOriginal = activeBitrates.length > 0 ? Math.max(...activeBitrates) : 0;
    const ratio =
      cap.proportional && topOriginal > cap.maxBitrate ? cap.maxBitrate / topOriginal : 1;

    const nextEncodings = encodings.map((enc) => {
      // Nunca altera `active` — ver regra crítica no topo do arquivo.
      if (enc.active === false) return enc;
      const original = enc.maxBitrate ?? cap.maxBitrate;
      const scaled = ratio < 1 ? Math.round(original * ratio) : original;
      const next: RTCRtpEncodingParameters = {
        ...enc,
        maxBitrate: Math.min(scaled, cap.maxBitrate),
      };
      if (typeof cap.maxFramerate === 'number') {
        next.maxFramerate = Math.min(enc.maxFramerate ?? cap.maxFramerate, cap.maxFramerate);
      }
      return next;
    });

    await sender.setParameters({
      ...params,
      encodings: nextEncodings,
    });
    return true;
  } catch {
    return false;
  }
}

/**
 * Limita maxBitrate (e opcionalmente maxFramerate) das encodings ativas da câmera.
 * Não remove camadas de simulcast — só evita que a camada alta consuma o perfil cheio.
 */
export async function capLocalCameraPublishBitrate(
  room: Room,
  cap: CameraPublishBitrateCap,
): Promise<boolean> {
  const sender = getCameraSender(room);
  if (!sender) return false;
  return capSenderEncodings(sender, cap);
}

/** Mesma operação a partir do track (fundo virtual / CPU constrained recebem o track). */
export async function capLocalVideoTrackPublishBitrate(
  track: LocalVideoTrack,
  cap: CameraPublishBitrateCap,
): Promise<boolean> {
  const sender = getTrackSender(track);
  if (!sender) return false;
  return capSenderEncodings(sender, cap);
}

export async function capLocalCameraForLargeRoom(room: Room): Promise<boolean> {
  return capLocalCameraPublishBitrate(room, {
    maxBitrate: LARGE_ROOM_CAMERA_MAX_BITRATE,
    maxFramerate: 20,
  });
}

export async function capLocalCameraForPoorNetwork(room: Room): Promise<boolean> {
  return capLocalCameraPublishBitrate(room, {
    maxBitrate: POOR_NETWORK_CAMERA_MAX_BITRATE,
    maxFramerate: 15,
  });
}

/**
 * Substituto de `LocalVideoTrack.prioritizePerformance()`: alivia encoder/CPU/uplink
 * (15 fps, ~400 kbps na camada alta, inferiores proporcionais) mantendo TODAS as
 * camadas ativas — o SFU continua vendo as 3 camadas e o score não cai para Poor.
 */
export async function applyLocalVideoTrackPerformanceCap(track: LocalVideoTrack): Promise<boolean> {
  return capLocalVideoTrackPublishBitrate(track, {
    maxBitrate: PERFORMANCE_CAMERA_MAX_BITRATE,
    maxFramerate: PERFORMANCE_CAMERA_MAX_FRAMERATE,
    proportional: true,
  });
}

export async function capLocalCameraForPerformance(room: Room): Promise<boolean> {
  return capLocalCameraPublishBitrate(room, {
    maxBitrate: PERFORMANCE_CAMERA_MAX_BITRATE,
    maxFramerate: PERFORMANCE_CAMERA_MAX_FRAMERATE,
    proportional: true,
  });
}

/** Câmera vira thumbnail enquanto este cliente compartilha tela. */
export async function capLocalCameraForScreenShare(room: Room): Promise<boolean> {
  return capLocalCameraPublishBitrate(room, {
    maxBitrate: SCREEN_SHARE_COEXIST_CAMERA_MAX_BITRATE,
    maxFramerate: SCREEN_SHARE_COEXIST_CAMERA_MAX_FRAMERATE,
    proportional: true,
  });
}

/** Restaura tetos de publish ao perfil efetivo (medium/high/max ± economy). */
export async function restoreLocalCameraPublishBitrate(room: Room): Promise<boolean> {
  const sender = getCameraSender(room);
  if (!sender) return false;
  return restoreSenderEncodings(sender);
}

export async function restoreLocalVideoTrackPublishBitrate(
  track: LocalVideoTrack,
): Promise<boolean> {
  const sender = getTrackSender(track);
  if (!sender) return false;
  return restoreSenderEncodings(sender);
}

async function restoreSenderEncodings(sender: RTCRtpSender): Promise<boolean> {
  const economy = readEconomyModeEnabled();
  const effective = getEffectiveVideoQualityProfile(readVideoQualityProfile(), economy);
  const capture = getVideoQualityRoomCapture(effective);
  const encoding = capture.publishDefaults.videoEncoding;
  const maxBitrate = encoding?.maxBitrate;
  if (typeof maxBitrate !== 'number' || maxBitrate <= 0) return false;

  // Camadas inferiores do perfil (h360, h180…) — ordem alta → baixa.
  const lowerLayerBitrates = (capture.publishDefaults.videoSimulcastLayers ?? []).map(
    (preset) => preset.encoding.maxBitrate,
  );

  try {
    const params = sender.getParameters();
    const encodings = params.encodings || [];
    if (encodings.length === 0) return false;

    const originals = originalEncodingsBySender.get(sender);
    const activeRanked = rankEncodingsHighToLow(encodings).filter(
      (index) => encodings[index].active !== false,
    );

    const next = encodings.map((enc) => ({ ...enc }));
    activeRanked.forEach((index, rank) => {
      const original = originals?.[index];
      // Com snapshot: volta ao fps original (mesmo que fosse indefinido = sem cap).
      const restoredFps = originals ? original?.maxFramerate : next[index].maxFramerate;
      if (rank === 0) {
        next[index] = {
          ...next[index],
          maxBitrate,
          maxFramerate: encoding?.maxFramerate ?? restoredFps,
        };
        return;
      }
      const fromProfile = lowerLayerBitrates[rank - 1];
      const restored = original?.maxBitrate ?? fromProfile;
      next[index] = {
        ...next[index],
        // Mantém camadas baixas abaixo do teto alto (não força subir demais).
        maxBitrate: Math.min(restored ?? next[index].maxBitrate ?? maxBitrate, maxBitrate),
        maxFramerate: restoredFps,
      };
    });

    await sender.setParameters({ ...params, encodings: next });
    return true;
  } catch {
    return false;
  }
}
